<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { CalendarCheck, CalendarMinus, Clock, FileText, Pencil, Trash2, UserRound } from 'lucide-vue-next'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useHrApi } from '../composables/useHrApi'
import EmployeeFilesSection from '../components/EmployeeFilesSection.vue'
import EmployeeCardsSection from '../components/EmployeeCardsSection.vue'
import { resolveMediaUrl } from '../utils/files'
import {
  attendanceStatusClass,
  attendanceStatusLabel,
  billingModeLabel,
  employeeTypeLabel,
  entryTypeLabel,
  formatMinutesAsHours,
  formatNumber,
  monthEnd,
  monthStart,
} from '../utils/format'
import type { AttendanceRecord, AttendanceReportRow, Employee, TimesheetEntry, TimesheetReportRow } from '../types/hr'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const { getEmployee, deleteEmployee, attendanceReport, timesheetReport, listAttendance, listTimesheets } = useHrApi()

const employeeId = computed(() => Number(route.params.id))
const permissions = computed(() => auth.user?.permissions ?? [])
const canUpdate = computed(() => permissions.value.includes('employees.update'))
const canDelete = computed(() => permissions.value.includes('employees.delete'))
const canManageAttendance = computed(() => permissions.value.includes('attendance.manage'))
const canManageTimesheets = computed(() => permissions.value.includes('timesheets.manage'))
const canViewCards = computed(() => permissions.value.includes('kiosk.view'))

const employee = ref<Employee | null>(null)
const supervisor = ref<Employee | null>(null)
const attendanceSummary = ref<AttendanceReportRow | null>(null)
const timesheetSummary = ref<TimesheetReportRow | null>(null)
const recentAttendance = ref<AttendanceRecord[]>([])
const recentTimesheets = ref<TimesheetEntry[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const showDeleteConfirm = ref(false)
const deleting = ref(false)

/** Section tabs: only the active section renders visibly. */
const activeTab = ref('profile')

const tabs = [
  { id: 'profile', label: 'hr.employeeDetail.tabProfile', icon: UserRound },
  { id: 'attendance', label: 'hr.employeeDetail.tabAttendance', icon: CalendarCheck },
  { id: 'hours', label: 'hr.employeeDetail.tabHours', icon: Clock },
  { id: 'documents', label: 'hr.employeeDetail.tabDocuments', icon: FileText },
]

async function load() {
  loading.value = true
  error.value = ''
  try {
    const loaded = await getEmployee(employeeId.value)
    employee.value = loaded

    const [attendanceRows, timesheetRows, attendanceTotals, timesheetTotals] = await Promise.all([
      listAttendance({ employee_id: employeeId.value, page: 1, per_page: 5 }),
      listTimesheets({ employee_id: employeeId.value, page: 1, per_page: 5 }),
      attendanceReport({ employee_id: employeeId.value, date_from: monthStart(), date_to: monthEnd() }),
      timesheetReport({ employee_id: employeeId.value, date_from: monthStart(), date_to: monthEnd() }),
    ])

    recentAttendance.value = attendanceRows.items
    recentTimesheets.value = timesheetRows.items
    attendanceSummary.value = attendanceTotals[0] ?? null
    timesheetSummary.value = timesheetTotals[0] ?? null

    if (loaded.supervisor_employee_id) {
      supervisor.value = await getEmployee(loaded.supervisor_employee_id).catch(() => null)
    }
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(load)

const attendanceRate = computed(() => {
  const summary = attendanceSummary.value
  if (!summary || summary.total_days === 0) return '—'
  return `${Math.round((summary.attended_days / summary.total_days) * 100)}%`
})

function name(): string {
  if (!employee.value) return ''
  const value = `${employee.value.last_name} ${employee.value.first_name}`.trim()
  return value || employee.value.employee_code
}

/** Initials shown when the employee has no photo. */
function initials(): string {
  if (!employee.value) return ''
  const first = employee.value.first_name?.trim().charAt(0) ?? ''
  const last = employee.value.last_name?.trim().charAt(0) ?? ''
  return `${last}${first}`.toUpperCase() || '—'
}

function supervisorName(): string {
  if (!supervisor.value) return ''
  const value = `${supervisor.value.last_name} ${supervisor.value.first_name}`.trim()
  return value || supervisor.value.employee_code
}

function rateFor(mode: string): string {
  if (!employee.value) return '—'
  if (mode === 'hourly') return formatNumber(employee.value.default_hourly_rate)
  if (mode === 'production_cycle') return formatNumber(employee.value.default_cycle_rate)
  return formatNumber(employee.value.default_fixed_salary)
}

function askDelete() {
  actionError.value = ''
  showDeleteConfirm.value = true
}

async function confirmDelete() {
  actionError.value = ''
  deleting.value = true
  try {
    await deleteEmployee(employeeId.value)
    showDeleteConfirm.value = false
    await router.push('/employees')
  } catch (cause) {
    actionError.value = formatApiError(cause)
    showDeleteConfirm.value = false
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/employees">← {{ t('hr.employeeDetail.back') }}</RouterLink>
    <div v-if="employee" class="profile-header">
      <img
        v-if="employee.photo_url"
        class="profile-photo"
        :src="resolveMediaUrl(employee.photo_url)"
        :alt="name()"
      >
      <div v-else class="profile-photo profile-photo--initials">{{ initials() }}</div>
      <div>
        <h2>
          {{ name() }}
          <span :class="['status-badge', employee.is_active === 1 ? 'status-ok' : 'status-neutral']">
            {{ employee.is_active === 1 ? t('hr.common.active') : t('hr.common.inactive') }}
          </span>
        </h2>
        <p>{{ employee.employee_code }} · {{ employee.job_title || t('hr.common.none') }}</p>
      </div>
    </div>
    <template v-else>
      <h2>{{ t('hr.employeeDetail.loading') }}</h2>
    </template>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('hr.employeeDetail.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('hr.common.retry') }}</button>
  </div>

  <template v-else-if="employee">
    <div class="toolbar">
      <div class="button-group" role="group" :aria-label="t('hr.employeeDetail.actionsLabel')">
        <RouterLink v-if="canUpdate" class="button-group__item is-primary" :to="`/employees/${employee.id}/edit`">
          <Pencil :size="16" aria-hidden="true" />
          {{ t('hr.common.edit') }}
        </RouterLink>
        <RouterLink
          v-if="canManageAttendance"
          class="button-group__item"
          :to="{ path: '/attendance', query: { employee: employee.id, action: 'new' } }"
        >
          <CalendarCheck :size="16" aria-hidden="true" />
          {{ t('hr.employeeDetail.actionAttendance') }}
        </RouterLink>
        <RouterLink
          v-if="canManageAttendance"
          class="button-group__item"
          :to="{ path: '/leave', query: { employee: employee.id, action: 'new' } }"
        >
          <CalendarMinus :size="16" aria-hidden="true" />
          {{ t('hr.employeeDetail.actionLeave') }}
        </RouterLink>
        <RouterLink
          v-if="canManageTimesheets"
          class="button-group__item"
          :to="{ path: '/overtime', query: { employee: employee.id, action: 'new' } }"
        >
          <Clock :size="16" aria-hidden="true" />
          {{ t('hr.employeeDetail.actionHours') }}
        </RouterLink>
      </div>
      <span class="spacer" />
      <div v-if="canDelete" class="button-group">
        <button class="button-group__item is-danger" type="button" @click="askDelete">
          <Trash2 :size="16" aria-hidden="true" />
          {{ t('hr.common.delete') }}
        </button>
      </div>
    </div>

    <p v-if="actionError" class="error-inline">{{ actionError }}</p>

    <nav class="tabs" :aria-label="t('hr.employeeDetail.tabsLabel')">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="tab-button"
        :class="{ 'is-active': activeTab === tab.id }"
        type="button"
        @click="activeTab = tab.id"
      >
        <component :is="tab.icon" :size="16" aria-hidden="true" />
        {{ t(tab.label) }}
      </button>
    </nav>

    <div v-show="activeTab === 'profile'" class="table-scroll tab-block">
      <table class="data-table">
        <tbody>
          <tr class="row-section">
            <th colspan="2">{{ t('hr.employeeDetail.employment') }}</th>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.employeeCode') }}</td>
            <td class="field-value cell-code">{{ employee.employee_code }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.common.status') }}</td>
            <td class="field-value">
              <span :class="['status-badge', employee.is_active === 1 ? 'status-ok' : 'status-neutral']">
                {{ employee.is_active === 1 ? t('hr.common.active') : t('hr.common.inactive') }}
              </span>
            </td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employees.department') }}</td>
            <td class="field-value">{{ employee.department_name || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.supervisor') }}</td>
            <td class="field-value">{{ supervisor ? supervisorName() : t('hr.employeeDetail.noSupervisor') }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employees.jobTitle') }}</td>
            <td class="field-value">{{ employee.job_title || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.employeeType') }}</td>
            <td class="field-value">{{ employeeTypeLabel(t, employee.employee_type) }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.hireDate') }}</td>
            <td class="field-value">{{ formatDisplayDate(employee.hire_date) || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.linkedUser') }}</td>
            <td class="field-value">{{ employee.user_name || t('hr.employeeDetail.noLinkedUser') }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.createdAt') }}</td>
            <td class="field-value">{{ formatDisplayDate(employee.created_at) || '—' }}</td>
          </tr>

          <tr class="row-section">
            <th colspan="2">{{ t('hr.employeeDetail.contact') }}</th>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.phone') }}</td>
            <td class="field-value">{{ employee.phone || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.email') }}</td>
            <td class="field-value">{{ employee.email || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.homeAddress') }}</td>
            <td class="field-value">{{ employee.home_address || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.emergencyName') }}</td>
            <td class="field-value">{{ employee.emergency_contact_name || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.emergencyNumber') }}</td>
            <td class="field-value">{{ employee.emergency_contact_number || '—' }}</td>
          </tr>

          <tr class="row-section">
            <th colspan="2">{{ t('hr.employeeDetail.personal') }}</th>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.birthday') }}</td>
            <td class="field-value">{{ formatDisplayDate(employee.birthday) || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.passport') }}</td>
            <td class="field-value">{{ employee.id_passport_number || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.taxId') }}</td>
            <td class="field-value">{{ employee.tax_id || '—' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.bankCard') }}</td>
            <td class="field-value">{{ employee.bank_card_number || '—' }}</td>
          </tr>

          <tr class="row-section">
            <th colspan="2">{{ t('hr.employeeDetail.compensation') }}</th>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.billingMode') }}</td>
            <td class="field-value">{{ billingModeLabel(t, employee.billing_mode) }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.hourlyRate') }}</td>
            <td class="field-value">{{ rateFor('hourly') }} {{ employee.currency_code ?? '' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.cycleRate') }}</td>
            <td class="field-value">{{ rateFor('production_cycle') }} {{ employee.currency_code ?? '' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.fixedSalary') }}</td>
            <td class="field-value">{{ rateFor('fixed_salary') }} {{ employee.currency_code ?? '' }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeForm.currency') }}</td>
            <td class="field-value">{{ employee.currency_code || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <EmployeeCardsSection
      v-if="activeTab === 'profile' && canViewCards"
      :employee-id="employee.id"
    />

    <div v-show="activeTab === 'documents'" class="tab-block">
      <EmployeeFilesSection :employee-id="employee.id" />
    </div>

    <div v-show="activeTab === 'attendance'" class="table-scroll tab-block">
      <table class="data-table">
        <tbody>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.daysAttended') }}</td>
            <td class="field-value">{{ attendanceSummary?.attended_days ?? 0 }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.daysAbsent') }}</td>
            <td class="field-value">{{ attendanceSummary?.absent_days ?? 0 }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.attendanceRate') }}</td>
            <td class="field-value">{{ attendanceRate }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.workedHours') }}</td>
            <td class="field-value">{{ formatMinutesAsHours(attendanceSummary?.total_worked_minutes ?? 0) }}</td>
          </tr>
          <tr class="row-section">
            <th colspan="2">{{ t('hr.employeeDetail.recentAttendance') }}</th>
          </tr>
          <tr v-if="recentAttendance.length === 0">
            <td colspan="2" class="empty-inline">{{ t('hr.employeeDetail.noAttendance') }}</td>
          </tr>
          <tr v-for="record in recentAttendance" :key="record.id">
            <td class="cell-date">{{ formatDisplayDate(record.attendance_date) }}</td>
            <td>
              <span :class="['status-badge', attendanceStatusClass(record.status)]">
                {{ attendanceStatusLabel(t, record.status) }}
              </span>
              <small class="cell-sub">
                {{ record.check_in_at ? record.check_in_at.slice(11, 16) : '—' }} –
                {{ record.check_out_at ? record.check_out_at.slice(11, 16) : '—' }} ·
                {{ formatMinutesAsHours(record.worked_minutes) }}
                <template v-if="record.notes"> · {{ record.notes }}</template>
              </small>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-show="activeTab === 'hours'" class="table-scroll tab-block">
      <table class="data-table">
        <tbody>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.totalHours') }}</td>
            <td class="field-value">{{ formatNumber(timesheetSummary?.total_hours ?? 0, 1) }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.entries') }}</td>
            <td class="field-value">{{ timesheetSummary?.total_entries ?? 0 }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('hr.employeeDetail.billAmount') }}</td>
            <td class="field-value">{{ formatNumber(timesheetSummary?.total_bill_amount ?? 0) }} {{ employee.currency_code ?? '' }}</td>
          </tr>
          <tr class="row-section">
            <th colspan="2">{{ t('hr.employeeDetail.recentTimesheets') }}</th>
          </tr>
          <tr v-if="recentTimesheets.length === 0">
            <td colspan="2" class="empty-inline">{{ t('hr.employeeDetail.noTimesheets') }}</td>
          </tr>
          <tr v-for="entry in recentTimesheets" :key="entry.id">
            <td class="cell-date">{{ formatDisplayDate(entry.work_date) }}</td>
            <td>
              <span class="cell-name">{{ entry.task_name }}</span>
              <small class="cell-sub">
                {{ entryTypeLabel(t, entry.entry_type) }} ·
                {{ formatNumber(entry.hours_worked, 2) }} h ·
                {{ formatNumber(entry.bill_amount) }} {{ entry.currency_code ?? '' }}
              </small>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>

  <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
    <div class="modal" role="dialog" aria-modal="true">
      <h3>{{ t('hr.employeeDetail.deleteTitle') }}</h3>
      <p>{{ t('hr.employeeDetail.deleteConfirm') }}</p>
      <div class="modal-actions">
        <button class="btn-secondary" type="button" @click="showDeleteConfirm = false">
          {{ t('hr.common.cancel') }}
        </button>
        <button class="btn-danger action-button" type="button" :disabled="deleting" @click="confirmDelete">
          <Trash2 :size="16" aria-hidden="true" />
          {{ t('hr.common.delete') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.section-title { margin: 24px 0 10px; font-size: 14px; font-weight: 700; color: #2b2027; }
h2 .status-badge { margin-left: 8px; vertical-align: middle; }
</style>
